import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AppContext } from '../App'
import { useIsMobile } from '../hooks/useIsMobile'
import { useGet } from '../hooks/useGet'
import { RadAppLayout } from '../common/RadAppLayout'
import { RadBox } from '../common/RadBox'
import { RadButton } from '../common/RadButton'
import { RadCards } from '../common/RadCards'
import { RadGrid } from '../common/RadGrid'
import { RadHeader } from '../common/RadHeader'
import { RadInput2 } from '../common/RadInput2'
import { RadLink } from '../common/RadLink'
import { RadMultiselect } from '../common/RadMultiselect'
import { RadPagination } from '../common/RadPagination'
import { RadPopover } from '../common/RadPopover'
import { RadSelect } from '../common/RadSelect'
import { RadSpaceBetween } from '../common/RadSpaceBetween'
import { EntityModal } from '../shared/EntityModal'
import { SortPopover } from '../shared/SortPopover'
import { formatDateTime, getShortText } from '../common/utilities'
import { FancyListHeader } from '../shared/FancyListHeader'
import { BottomPagination } from '../shared/BottomPagination'
import { exportToCsv } from '../common/exportToCsv'

export function ProjectList ({ selected }) {
  const { loadingCount, user } = useContext(AppContext)
  const pageLength = 20
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [selectedEntity, setSelectedEntity] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [targetAreaFilteringText, setTargetAreaFilteringText] = useState()
  const [targetPopulationFilteringText, setTargetPopulationFilteringText] = useState('')
  const searchText = searchParams.get('search') ?? ''
  const sectorId = searchParams.get('sector') ?? ''
  const tagIds = searchParams.get('tags') ?? ''
  const projectTypeId = searchParams.get('project-type') ?? ''
  const priorityAreaId = searchParams.get('priority-area') ?? ''
  const priorityPopulationIds = searchParams.get('priority-populations') ?? ''
  const dashFundingProgramId = searchParams.get('dash-funding-program') ?? ''
  const sortOrder = searchParams.get('sort') ?? 'alphabetical'
  const currentPageIndex = searchParams.get('page') != null ? parseInt(searchParams.get('page')) : 1
  const { data: projects, count } = useGet([
    '/api/project',
    `?search=${searchText}`,
    `&sectorId=${sectorId}`,
    `&tagIds=${tagIds ?? ''}`,
    `&projectTypeId=${projectTypeId}`,
    `&priorityAreaId=${priorityAreaId}`,
    `&priorityPopulationIds=${priorityPopulationIds}`,
    `&dashFundingProgramId=${dashFundingProgramId}`,
    `&limit=${pageLength}` +
    `&offset=${(currentPageIndex - 1) * pageLength}`,
    `&sortOrder=${sortOrder}` + '&export=true'
  ].join(''))

  const { data: dashFundingProgramOptions } = useGet('/api/option/type?entity=DASH_funding_program&&all=true')
  const { data: projectTypeOptions } = useGet('/api/option/type?entity=project_use_case&all=true')
  const { data: sectorOptions } = useGet('/api/option/sector?all=true')
  const { data: tags } = useGet('/api/tag?limit=2000')
  const { data: targetAreaOptions } = useGet(
    targetAreaFilteringText !== ''
      ? `/api/option/target-area?search=${targetAreaFilteringText}&all=true`
      : `/api/option/target-area?search=${targetAreaFilteringText}&all=true&limit=0`
  )
  const { data: targetPopulationOptions } = useGet(`/api/option/priority-population?search=${targetPopulationFilteringText}&limit=500`)
  const { data: priorityArea } = useGet(priorityAreaId != null && priorityAreaId !== '' ? `/api/target-area/${priorityAreaId}` : null)
  const { data: userInfo } = useGet('/api/user/current')

  useEffect(() => {
    for (const [key, value] of searchParams) {
      if (value === '' || value === null) {
        searchParams.delete(key)
      }
    }
    if (searchParams.get('projectId') != null) {
      setSelectedEntity({ type: 'project', id: parseInt(searchParams.get('projectId')) })
      searchParams.delete('projectId')
      setSearchParams(searchParams)
    }
  }, [searchParams])

  if (
    projects != null &&
    sectorOptions != null &&
    targetAreaOptions != null &&
    targetPopulationOptions != null &&
    tags != null &&
    userInfo != null
  ) {
    const tagOptions = tags.map((x) => { return { value: x.id.toString(), label: x.name } })
    const canCreate = userInfo.roles.some((x) => x.name === 'Admin') ||
      userInfo.roles.some((x) => x.permissions.some((y) => y.name === 'Project Create'))
    const publicUser = userInfo.roles.some((x) => x.name === 'Public User') && userInfo.roles.length === 1

    async function exportProjects () {
      await exportToCsv({
        uri: [
          '/api/project',
          `?search=${searchText}`,
          `&sectorId=${sectorId}`,
          `&tagIds=${tagIds ?? ''}`,
          `&projectTypeId=${projectTypeId}`,
          `&priorityAreaId=${priorityAreaId}`,
          `&priorityPopulationIds=${priorityPopulationIds}`,
          `&dashFundingProgramId=${dashFundingProgramId}`,
          `&sortOrder=${sortOrder}`,
          '&export=true'
        ].join(''),
        columnNames: [
          'Name',
          'Description',
          'Highlights',
          'Funding Program',
          'Funding Start Date',
          'Funding End Date',
          'Sectors',
          'Organizations',
          'Priority Areas',
          'Priority Populations',
          'Project Types',
          'Collaboratives',
          'Framework Tags',
          'Topics'
        ],
        mapper: (items) => items.map(x => ([
          x.name,
          x.description,
          x.deliverables,
          x.dashFundingProgram,
          x.startDate,
          x.endDate,
          x.sectors,
          x.organizations,
          x.priorityAreas,
          x.priorityPopulations,
          x.projectTypes,
          x.collaboratives,
          x.frameworkTags,
          x.topics
        ])),
        filenamePrefix: 'projects',
        user,
        onError: (e) => console.error(e)
      })
    }

    return (
      <RadAppLayout
        contentHeader={
          <FancyListHeader
            title='Projects'
            count={count}
            actions={
              <RadSpaceBetween direction='horizontal' size='xs'>
                {!publicUser && <RadButton disabled={!canCreate} onClick={() => navigate('/project/create')}>Create</RadButton>}
                <RadButton onClick={exportProjects}>Export</RadButton>
              </RadSpaceBetween>
            }
            description={
              <RadBox>
                "Projects" are initiatives implemented by multisector collaborations around the country which received funding by DASH. Browse or search to find ideas, goals, or outcomes that resonate with your own work.
              </RadBox>
            }
          />
        }
        content={
          <RadSpaceBetween size='l'>
            <RadGrid
              gridDefinition={[
                { colspan: { default: 12, xs: 8 } },
                { colspan: { default: 12, xs: 4 } }
              ]}
            >
              <RadSpaceBetween direction='horizontal' size='xs'>
                <SortPopover
                  sortOrder={sortOrder}
                  loadingCount={loadingCount}
                />
                <FilterPopover
                  tagIds={tagIds}
                  sectorId={sectorId}
                  projectTypeId={projectTypeId}
                  dashFundingProgramId={dashFundingProgramId}
                  priorityAreaId={priorityAreaId}
                  priorityPopulationIds={priorityPopulationIds}
                  targetAreaFilteringText={targetAreaFilteringText}
                  setTargetAreaFilteringText={setTargetAreaFilteringText}
                  targetPopulationFilteringText={targetPopulationFilteringText}
                  setTargetPopulationFilteringText={setTargetPopulationFilteringText}
                  dashFundingProgramOptions={dashFundingProgramOptions}
                  projectTypeOptions={projectTypeOptions}
                  sectorOptions={sectorOptions}
                  tagOptions={tagOptions}
                  targetAreaOptions={targetAreaOptions}
                  targetPopulationOptions={targetPopulationOptions}
                  priorityArea={priorityArea}
                  loadingCount={loadingCount}
                />
                <RadBox className='rad-search-input'>
                  <RadInput2
                    initialValue={searchText}
                    placeholder='Search projects to sort by relevance'
                    type='search'
                    onEnter={({ detail }) => {
                      if (detail.value !== '') {
                        searchParams.set('search', detail.value)
                      } else {
                        searchParams.delete('search')
                      }
                      if (detail.value === '') {
                        searchParams.delete('sort')
                      } else {
                        searchParams.set('sort', 'relevance')
                      }
                      searchParams.delete('page')
                      setSearchParams(searchParams)
                    }}
                  />
                </RadBox>
              </RadSpaceBetween>
              <RadBox float={isMobile ? 'none' : 'right'}>
                <RadPagination
                  currentPageIndex={currentPageIndex}
                  pagesCount={Math.ceil(count / pageLength)}
                  onChange={({ detail }) => {
                    searchParams.set('page', detail.currentPageIndex)
                    setSearchParams(searchParams)
                  }}
                  ariaLabels={{
                    nextPageLabel: 'Next page',
                    previousPageLabel: 'Previous page',
                    pageLabel: pageNumber => `Page ${pageNumber} of all pages`
                  }}
                />
              </RadBox>
            </RadGrid>
            {
              (sectorId !== '' || tagIds !== '' || projectTypeId !== '' || dashFundingProgramId !== '' || priorityArea != null || priorityPopulationIds !== '') &&
                <RadBox color='text-status-inactive'>
                  {'Filter(s): ' +
                   [
                     sectorId !== '' ? `Sector: ${sectorOptions.find(x => x.value === sectorId)?.label}` : null,
                     tagIds !== '' ? `Framework Tags: ${tagOptions.filter(x => tagIds.split('-').includes(x.value)).map(x => x.label).join(', ')}` : null,
                     projectTypeId !== '' ? 'Type: ' + projectTypeOptions.find(x => x.value === projectTypeId)?.label : null,
                     dashFundingProgramId !== '' ? 'Funding Program: ' + dashFundingProgramOptions.find(x => x.value === dashFundingProgramId)?.label : null,
                     priorityArea != null ? 'Priority Area: ' + priorityArea.name : null,
                     priorityPopulationIds !== ''
                       ? 'Priority Populations: ' + targetPopulationOptions
                         .filter(x => priorityPopulationIds.split('-').includes(x.value)).map(x => x.label).join(', ')
                       : null
                   ]
                     .filter(x => x !== null)
                     .join(', ')}
                </RadBox>
            }
            <RadCards
              className='project-cards'
              cardDefinition={{
                header: item => {
                  if (isMobile) {
                    return (
                      <RadLink className='font-weight-700' fontSize='heading-s' href={`project/${item.id}`}>
                        {item.name}
                      </RadLink>
                    )
                  } else {
                    return (
                      <RadButton
                        onClick={() => setSelectedEntity({ type: 'project', id: item.id })}
                        className='font-size-24'
                        variant='inline-link'
                      >
                        {item.name}
                      </RadButton>
                    )
                  }
                },
                sections: [
                  {
                    id: 'description',
                    className: 'description',
                    content: item => getShortText(item.description)
                  },
                  {
                    id: 'sectors',
                    content: item => <RadBox fontWeight='bold'>{item.sectors?.join(', ')}</RadBox>
                  },
                  {
                    id: 'updatedAt',
                    content: item => <RadBox color='text-status-inactive'>{`Updated: ${formatDateTime(item.updatedAt)}`}</RadBox>
                  }
                ]
              }}
              cardsPerRow={[
                { cards: 1 },
                { minWidth: 640, cards: 2 },
                { minWidth: 960, cards: 3 },
                { minWidth: 1280, cards: 4 }
              ]}
              items={projects}
              variant='full-page'
              empty={
                <RadBox textAlign='center' color='inherit' padding={{ top: 'xxl' }}>
                  No records meet this search criteria.
                </RadBox>
              }
            />
            <BottomPagination
              currentPageIndex={currentPageIndex}
              count={count}
              pageLength={pageLength}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
            <EntityModal
              selectedEntity={selectedEntity}
              setSelectedEntity={setSelectedEntity}
              closeModal={() => setSelectedEntity(null)}
            />
          </RadSpaceBetween>
        }
      />
    )
  }
}

function FilterPopover ({
  dashFundingProgramId,
  dashFundingProgramOptions,
  loadingCount,
  organizationTypeOptions,
  priorityArea,
  priorityAreaId,
  priorityPopulationIds,
  projectTypeId,
  projectTypeOptions,
  sectorId,
  sectorOptions,
  setTargetAreaFilteringText,
  tagIds,
  tagOptions,
  targetAreaFilteringText,
  targetAreaOptions,
  targetPopulationOptions
}) {
  const [searchParams, setSearchParams] = useSearchParams()

  return (
    <RadPopover
      position='bottom'
      size='large'
      triggerType='custom'
      content={
        <RadSpaceBetween size='xs'>
          <RadHeader
            variant='h2'
          >
            Filter
          </RadHeader>
          <RadSelect
            disabled={loadingCount > 0}
            filteringType='auto'
            selectedOption={sectorOptions.find(x => x.value === sectorId) ?? sectorOptions[0]}
            onChange={({ detail }) => {
              if (detail.selectedOption.value === '') {
                searchParams.delete('sector')
              } else {
                searchParams.set('sector', detail.selectedOption.value)
              }
              searchParams.delete('page')
              setSearchParams(searchParams)
            }}
            options={sectorOptions}
            enteredTextLabel={value => value}
            selectedAriaLabel='Selected'
            placeholder='Choose a record type'
            empty='No matches found'
          />
          <RadMultiselect
            disabled={loadingCount > 0}
            filteringType='auto'
            selectedOptions={tagOptions.filter((x) => tagIds.split('-').includes(x.value))}
            onChange={({ detail }) => {
              const ids = detail.selectedOptions.map((x) => x.value).join('-')
              if (ids === '') {
                searchParams.delete('tags')
              } else {
                searchParams.set('tags', ids)
              }
              searchParams.delete('page')
              setSearchParams(searchParams)
            }}
            options={tagOptions}
            selectedAriaLabel='Selected'
            deselectAriaLabel={e => `Remove ${e.label}`}
            placeholder='Choose framework tags'
            keepOpen={false}
          />
          <RadSelect
            disabled={loadingCount > 0}
            filteringType='manual'
            selectedOption={priorityArea != null ? { value: priorityArea.id, label: priorityArea.name } : targetAreaOptions[0]}
            onChange={({ detail }) => {
              if (detail.selectedOption.value === '') {
                searchParams.delete('priority-area')
              } else {
                searchParams.set('priority-area', detail.selectedOption.value)
              }
              searchParams.delete('page')
              setSearchParams(searchParams)
            }}
            onLoadItems={({ detail }) => {
              setTargetAreaFilteringText(detail.filteringText)
            }}
            options={targetAreaOptions}
            filteringPlaceholder='Enter zip code or location name'
            selectedAriaLabel='Selected'
            empty='No matches found'
          />
          <RadMultiselect
            disabled={loadingCount > 0}
            filteringType='auto'
            selectedOptions={targetPopulationOptions.filter((x) => priorityPopulationIds.split('-').includes(x.value))}
            onChange={({ detail }) => {
              const ids = detail.selectedOptions.map((x) => x.value).join('-')
              if (ids === '') {
                searchParams.delete('priority-populations')
              } else {
                searchParams.set('priority-populations', ids)
              }
              searchParams.delete('page')
              setSearchParams(searchParams)
            }}
            options={targetPopulationOptions}
            selectedAriaLabel='Selected'
            deselectAriaLabel={e => `Remove ${e.label}`}
            placeholder='Choose priority populations'
            keepOpen={false}
          />
          <RadSelect
            disabled={loadingCount > 0}
            selectedOption={projectTypeOptions.find(x => x.value === projectTypeId) ?? projectTypeOptions[0]}
            onChange={({ detail }) => {
              if (detail.selectedOption.value === '') {
                searchParams.delete('project-type')
              } else {
                searchParams.set('project-type', detail.selectedOption.value)
              }
              searchParams.delete('page')
              setSearchParams(searchParams)
            }}
            options={projectTypeOptions}
            selectedAriaLabel='Selected'
            empty='No matches found'
          />
          <RadSelect
            disabled={loadingCount > 0}
            filteringType='auto'
            selectedOption={dashFundingProgramOptions.find(x => x.value === dashFundingProgramId) ?? dashFundingProgramOptions[0]}
            onChange={({ detail }) => {
              if (detail.selectedOption.value === '') {
                searchParams.delete('dash-funding-program')
              } else {
                searchParams.set('dash-funding-program', detail.selectedOption.value)
              }
              searchParams.delete('page')
              setSearchParams(searchParams)
            }}
            options={dashFundingProgramOptions}
            selectedAriaLabel='Selected'
            empty='No matches found'
          />
          <RadBox float='right'>
            <RadButton
              onClick={() => {
                searchParams.delete('sector')
                searchParams.delete('tags')
                searchParams.delete('record-type')
                searchParams.delete('resource-type')
                searchParams.delete('organization-type')
                searchParams.delete('project-type')
                searchParams.delete('priority-area')
                searchParams.delete('priority-populations')
                searchParams.delete('dash-funding-program')
                searchParams.delete('page')
                setSearchParams(searchParams)
              }}
            >
              Clear All Filters
            </RadButton>
          </RadBox>
        </RadSpaceBetween>
      }
    >
      <RadButton
        className='secondary'
        iconAlign='right'
        iconName='angle-down'
      >
        Filter
      </RadButton>
    </RadPopover>
  )
}
